/**
 * Lets a sticky column that is taller than the viewport scroll with the page.
 * Scrolling down moves it up until its bottom edge is in view, then it sticks there;
 * scrolling back up brings its top back into view. Columns that fit stay pinned to the top.
 * Applies to `[data-sticky-details]` on desktop widths.
 */
const BOTTOM_GAP = 24;
const desktop = window.matchMedia('(min-width: 750px)');

/** @param {HTMLElement} element */
function enhance(element) {
  if (element.dataset.stickyDetailsReady) return;
  element.dataset.stickyDetailsReady = 'true';

  let baseTop = 0;
  let currentTop = 0;
  let lastScroll = 0;
  let frame = 0;

  /** @returns {Element} The element that scrolls the page around this column. */
  const getScroller = () => {
    let node = element.parentElement;
    while (node && node !== document.body) {
      const { overflowY } = getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const measureBase = () => {
    element.style.removeProperty('top');
    // An always-visible header covers the top of the viewport, so the column rests below it
    const header = document.querySelector('#header-component');
    const headerOffset = header instanceof HTMLElement && header.getAttribute('sticky') === 'always' ? header.offsetHeight : 0;
    baseTop = Math.max(parseFloat(getComputedStyle(element).top) || 0, headerOffset);
  };

  const update = () => {
    frame = 0;
    if (!desktop.matches) {
      element.style.removeProperty('top');
      return;
    }

    const scroll = getScroller().scrollTop;
    const delta = scroll - lastScroll;
    lastScroll = scroll;

    const minTop = Math.min(baseTop, window.innerHeight - element.offsetHeight - BOTTOM_GAP);
    if (minTop >= baseTop) {
      currentTop = baseTop;
      element.style.removeProperty('top');
      return;
    }

    currentTop = Math.min(baseTop, Math.max(minTop, currentTop - delta));
    element.style.top = `${currentTop}px`;
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  const reset = () => {
    measureBase();
    currentTop = baseTop;
    lastScroll = getScroller().scrollTop;
    schedule();
  };

  reset();
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', reset, { passive: true });
  desktop.addEventListener('change', reset);
  new ResizeObserver(schedule).observe(element);
}

for (const element of document.querySelectorAll('[data-sticky-details]')) {
  if (element instanceof HTMLElement) enhance(element);
}
